/**
 * O que a tela de ordens de serviço DIZ — fora do componente, onde um teste
 * alcança. Nenhuma conta de tempo mora aqui: o tempo por etapa vem da view
 * `oficina.tempo_por_etapa`, e somar espera e reparo é só somar o que ela deu.
 */
import type { Etapa, OrdemDaApi, PassagemDeEtapa, TipoDeOrdem } from './api';

export const ROTULO_DA_ETAPA: Readonly<Record<Etapa, string>> = {
  solicitada: 'Solicitada',
  triagem: 'Triagem',
  diagnostico: 'Diagnóstico',
  planejada: 'Planejada',
  aguardando_peca: 'Aguardando peça',
  em_execucao: 'Em execução',
  teste: 'Em teste',
  liberada: 'Liberada',
  encerrada: 'Encerrada',
  cancelada: 'Cancelada',
};

/** As etapas que se escolhem à mão. Liberar, encerrar e cancelar têm botão próprio. */
export const ETAPAS_DE_TRABALHO: readonly Etapa[] = [
  'solicitada',
  'triagem',
  'diagnostico',
  'planejada',
  'aguardando_peca',
  'em_execucao',
  'teste',
];

export const ROTULO_DO_TIPO: Readonly<Record<TipoDeOrdem, string>> = {
  preventiva: 'Preventiva',
  corretiva: 'Corretiva',
  inspecao: 'Inspeção',
  campanha: 'Campanha',
  melhoria: 'Melhoria',
  terceiro: 'Terceiro',
};

/** Prioridade com significado, como a migração 0019 a define. */
export const ROTULO_DA_PRIORIDADE: Readonly<Record<number, string>> = {
  1: 'Segurança',
  2: 'Máquina parada',
  3: 'Normal',
  4: 'Programável',
};

export function estaFechada(etapa: Etapa): boolean {
  return etapa === 'encerrada' || etapa === 'cancelada';
}

export function podeLiberar(etapa: Etapa): boolean {
  return etapa === 'em_execucao' || etapa === 'teste';
}

/**
 * O PISO do diagnóstico e da causa raiz (migração 0101): o mesmo número de
 * `oficina.causa_registrada`, e o doctor (H53) cobra que os dois batam. Abaixo
 * dele o campo vira "x", "ok", "ver", e o histórico de causas fica vazio com
 * cara de cheio.
 */
export const CAUSA_MINIMA = 10;

/** O diagnóstico ou a causa raiz escritos de verdade. */
export function causaEscrita(texto: string | null | undefined): boolean {
  return (texto ?? '').trim().length >= CAUSA_MINIMA;
}

/**
 * A O.S. que só libera e encerra com diagnóstico e causa raiz (hoje, a
 * corretiva) e ainda não os tem. Quem diz se o tipo exige é o servidor
 * (`exigeCausa`); a tela só confere o que está escrito.
 */
export function faltaCausa(o: Pick<OrdemDaApi, 'exigeCausa' | 'diagnostico' | 'causaRaiz'>): boolean {
  return o.exigeCausa === true && !(causaEscrita(o.diagnostico) && causaEscrita(o.causaRaiz));
}

/** O erro do campo, AO SAIR dele (p.16): diz o que escrever. */
export function erroDaCausa(campo: 'diagnostico' | 'causa', texto: string): string | null {
  if (causaEscrita(texto)) return null;
  return campo === 'diagnostico'
    ? `Escreva o que estava errado na máquina, com pelo menos ${String(CAUSA_MINIMA)} caracteres.`
    : `Escreva por que a máquina falhou, com pelo menos ${String(CAUSA_MINIMA)} caracteres.`;
}

/** "O.S. 000124" — o manual escreve assim, com seis dígitos. */
export function numeroDaOrdem(numero: number): string {
  return `O.S. ${String(numero).padStart(6, '0')}`;
}

export interface TempoDaOrdem {
  readonly espera: number;
  readonly reparo: number;
}

/**
 * Quanto da O.S. foi ESPERA e quanto foi REPARO — o benefício que o v6 dá à
 * ferramenta 2 do módulo 06. Liberada e fechamento não entram em nenhum dos dois.
 */
export function tempoDaOrdem(etapas: readonly PassagemDeEtapa[]): TempoDaOrdem {
  let espera = 0;
  let reparo = 0;
  for (const e of etapas) {
    if (e.natureza === 'espera') espera += e.horas;
    else if (e.natureza === 'reparo') reparo += e.horas;
  }
  return { espera, reparo };
}

export interface ContagemDaOficina {
  readonly abertas: number;
  readonly maquinasParadas: number;
  readonly aguardandoPeca: number;
  readonly paraEncerrar: number;
}

/** A faixa do topo. Máquina parada conta MÁQUINAS, não O.S. */
export function contarOficina(ordens: readonly OrdemDaApi[]): ContagemDaOficina {
  const abertas = ordens.filter((o) => !estaFechada(o.etapa) && o.etapa !== 'liberada');
  return {
    abertas: abertas.length,
    maquinasParadas: new Set(abertas.filter((o) => o.maquinaParada).map((o) => o.equipamentoId)).size,
    aguardandoPeca: abertas.filter((o) => o.etapa === 'aguardando_peca').length,
    paraEncerrar: ordens.filter((o) => o.etapa === 'liberada').length,
  };
}

/**
 * A BASE de "Máquinas paradas" (p.17: o número sozinho não significa nada):
 * quantas máquinas estão na oficina, isto é, com O.S. aberta. Conta MÁQUINAS,
 * como o indicador que ela explica; a liberada já devolveu a máquina.
 */
export function contarMaquinasNaOficina(ordens: readonly OrdemDaApi[]): number {
  return new Set(
    ordens.filter((o) => !estaFechada(o.etapa) && o.etapa !== 'liberada').map((o) => o.equipamentoId),
  ).size;
}
